import queryString from 'query-string'
import { dateFns as moment } from '@pcln/date-fns-utils'

const travelerTypesQueryMap = {
  adults: 'adults',
  children: 'children',
  lapInfants: 'infants',
}

/**
 * Returns updated URL with valid parameters, used by executeSearch and elsewhere
 * @param state
 * @param urlRoot
 * @returns {string}
 */
export function buildURL(state, urlRoot = '/') {
  if (!validateForm(state)) {
    return ''
  }

  const slices = parseSlices(state.slices)

  const travelers = parseTravelers(state.travelers)

  const queryParams = Object.assign(state.queryParams, travelers, {
    'cabin-class': state.cabinClass,
  })

  // Add search-type query param to new search
  queryParams['search-type'] = buildSearchType(state.slices)

   // Add userActionType query param to new search for rebook
   if (state.modifyNow === 'MODIFY') {
    queryParams['userActionType'] = 'MODIFY'
   }

  // Only add alt-dates param if true
  if (state.flexDates) {
    queryParams['alt-dates'] = true
  } else {
    queryParams['alt-dates'] = false
  }

  // Add preferred airline query param
  if (state.preferredAirline && state.preferredAirline !== 'none') {
    queryParams['preferred-airline'] = state.preferredAirline
  }

  return `${urlRoot}${slices}?${queryString.stringify(queryParams)}`
}

export function executeSearch(state, urlRoot = '/', openNewTab = false) {
  return new Promise((resolve, reject) => {
    const url = buildURL(state, urlRoot)
    if (!url) {
      reject('Invalid form data')
    }

    if (openNewTab) {
      window.open(url, '_blank')
    } else {
      window.location.href = url
    }

    resolve(url)
  })
}

/**
 * Creates a search type string by concatenating the value for each orig/dest for all slices. The value
 * will either be 1 if a stop is a GDS city, and 0 otherwise. Defaults to 1 if search type is undefined.
 * @param slices
 */
function buildSearchType(slices) {
  return slices.reduce((searchType, slice) => {
    const origType =
      slice.origSearchType !== undefined ? slice.origSearchType : '1'
    const destType =
      slice.destSearchType !== undefined ? slice.destSearchType : '1'

    return `${searchType}${origType}${destType}`
  }, '')
}

/**
 * Return true if form is valid and ready to open the search URL, false otherwise.
 * @param slices
 * @param travelers
 * @returns {boolean}
 */
function validateForm({ slices, travelers }) {
  for (let i = 0; i < slices.length; i++) {
    const slice = slices[i]

    // Each slice must have an origin, destination, and date
    if (!(slice.origAirportCode && slice.destAirportCode && slice.date)) {
      return false
    }

    // Origin and destination of each slice must not match
    if (slice.origAirportCode === slice.destAirportCode) {
      return false
    }
  }

  if (travelers && travelers.errorMessage) {
    return false
  }

  // At least one traveler must be selected
  return Object.keys(travelers).reduce((acc, cur) => {
    return typeof travelers[cur] === 'number' ? acc + travelers[cur] : acc
  }, 0) >= 1
}

/**
 * Convert array of objects each having a date, origin, and destination to a string separating each
 * slice with a "/". Each slice will be in the format of "<origin>-<dest>-<YYYYMMDD>".
 * @param slices
 */
function parseSlices(slices) {
  return slices.reduce((acc, slice) => {
    const dateStr = moment(slice.date).format('YYYYMMDD')
    const sliceStr = `${slice.origAirportCode}-${slice.destAirportCode}-${dateStr}`
    return `${acc}${sliceStr}/`
  }, '')
}

/**
 * Returns object containing entry for each type of traveler in the given object, prepending "num-" to each key
 * in the given object.
 * @param travelers
 * @returns {{}}
 */
function parseTravelers(travelers) {
  return Object.keys(travelers).reduce((acc, travelerType) => {
    const queryKey = travelerTypesQueryMap[travelerType]
    if (queryKey) {
      acc[`num-${queryKey}`] = travelers[travelerType]
    }
    return acc
  }, {})
}
